import React, { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./lib/auth";
import { I18nProvider } from "./lib/i18n";
import Landing from "./pages/Landing";
import { Login, Register } from "./pages/Auth";
import Marketplace from "./pages/Marketplace";
import ProductDetail from "./pages/ProductDetail";
import { OrdersList, OrderDetail } from "./pages/Orders";
import Dashboard from "./pages/Dashboard";
import SupplierSubmit from "./pages/SupplierSubmit";
import Chat from "./pages/Chat";
import Admin from "./pages/Admin";
import Account from "./pages/Account";
import { ForgotPassword, ResetPassword } from "./pages/PasswordReset";
import DemandBoard from "./pages/DemandBoard";
import AvailableJobs from "./pages/AvailableJobs";
import SiteFooter from "./components/SiteFooter";
import Policy from "./pages/Policy";
import Members from "./pages/Members";
import { CurrencyProvider } from "./lib/currency";
import MaintenanceGate from "./components/MaintenanceGate";
import SupplierContactGate from "./components/SupplierContactGate";

function NoSupplier({ children }) {
  const { user } = useAuth();
  if (user?.role === "supplier") return <Navigate to="/dashboard" replace />;
  return children;
}

function Protected({ children, roles }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <div className="min-h-screen bg-[#101114] text-[#A8A8AD] p-10">Loading...</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;
  if (user.two_factor_mandatory && !user.totp_enabled && location.pathname !== "/account") return <Navigate to="/account" replace />;
  return children;
}

function ScrollTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

export default function App() {
  return (
    <I18nProvider>
      <AuthProvider>
        <CurrencyProvider>
        <div className="App">
          <Toaster position="bottom-right" theme="dark" toastOptions={{ style: { background: "#202127", color: "#F5F3ED", border: "1px solid #35353B" } }} />
          <BrowserRouter>
            <ScrollTop />
            <MaintenanceGate>
            <SupplierContactGate>
            <Routes>
              <Route path="/" element={<NoSupplier><Landing /></NoSupplier>} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/marketplace" element={<NoSupplier><Marketplace /></NoSupplier>} />
              <Route path="/product/:id" element={<NoSupplier><ProductDetail /></NoSupplier>} />
              <Route path="/orders" element={<Protected roles={["owner","staff","buyer"]}><OrdersList /></Protected>} />
              <Route path="/orders/:id" element={<Protected roles={["owner","staff","buyer"]}><OrderDetail /></Protected>} />
              <Route path="/demand-board" element={<Protected roles={["owner","staff"]}><DemandBoard /></Protected>} />
              <Route path="/jobs" element={<Protected roles={["supplier"]}><AvailableJobs /></Protected>} />
              <Route path="/dashboard" element={<Protected roles={["owner","staff","supplier"]}><Dashboard /></Protected>} />
              <Route path="/supplier/submit" element={<Protected roles={["supplier"]}><SupplierSubmit /></Protected>} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/account" element={<Protected><Account /></Protected>} />
              <Route path="/admin" element={<Protected roles={["owner"]}><Admin /></Protected>} />
              <Route path="/policy/:kind" element={<Policy />} />
              <Route path="/members" element={<Protected roles={["owner","staff"]}><Members /></Protected>} />
              <Route path="/chat" element={<Protected><Chat /></Protected>} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            <SiteFooter />
            </SupplierContactGate>
            </MaintenanceGate>
          </BrowserRouter>
        </div>
        </CurrencyProvider>
      </AuthProvider>
    </I18nProvider>
  );
}
